import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CodeSnapshot,
  Escalation,
  EscalationSeverity,
  HITL_NOTE_MAX_LENGTH,
  isHitlUnavailable,
} from '../../services/hitl';
import { useHitlApi } from './HitlApiContext';

const HITL_AMBER = '#B8600B';
const HITL_AMBER_LIGHT = '#F5E6D8';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onEscalated: (escalation: Escalation) => void;
  ensureTeachingSession: () => Promise<string>;
  lectureId?: string;
  getCodeSnapshot?: () => CodeSnapshot | undefined;
}

const SEVERITY_OPTIONS: { value: EscalationSeverity; label: string; description: string }[] = [
  {
    value: 'low',
    label: 'A quick check',
    description: 'I mostly understand this and want a second pair of eyes.',
  },
  {
    value: 'medium',
    label: 'Walk me through it',
    description: 'ako.hitl_severity_medium',
  },
  {
    value: 'high',
    label: 'I am blocked',
    description: 'I cannot move forward without help.',
  },
];

const HitlRequestModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onEscalated,
  ensureTeachingSession,
  lectureId,
  getCodeSnapshot,
}) => {
  const { t } = useTranslation();
  const hitlApi = useHitlApi();
  const [severity, setSeverity] = useState<EscalationSeverity>('medium');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, submitting]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const sourceTeachingSessionId = await ensureTeachingSession();
      const escalation = await hitlApi.requestOrRecover({
        source_teaching_session_id: sourceTeachingSessionId,
        source_lecture_id: lectureId,
        code_snapshot: getCodeSnapshot?.(),
        learner_note: note,
        severity,
      });
      onEscalated(escalation);
    } catch (err) {
      setError(
        isHitlUnavailable(err)
          ? 'Human instructor support is not enabled in this environment.'
          : err instanceof Error
            ? err.message
            : 'Human support could not be requested',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="hitl-modal-title"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target && !submitting) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(9, 9, 11, 0.56)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 24,
          maxWidth: 480,
          width: '100%',
          color: '#18181B',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          boxShadow: '0 20px 40px rgba(0,0,0,0.22)',
        }}
      >
        <h2 id="hitl-modal-title" style={{ margin: '0 0 8px', fontSize: 20 }}>
          Ask a human instructor
        </h2>
        <p style={{ color: '#52525B', margin: '0 0 16px', fontSize: 14, lineHeight: 1.5 }}>
          {t('ako.hitl_intro', { name: t('companion.name') })}
        </p>

        <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
          <legend style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>What do you need?</legend>
          {SEVERITY_OPTIONS.map((option) => {
            const selected = severity === option.value;
            return (
              <label
                key={option.value}
                style={{
                  display: 'block',
                  padding: 12,
                  marginBottom: 8,
                  borderRadius: 8,
                  border: `1px solid ${selected ? HITL_AMBER : '#D4D4D8'}`,
                  background: selected ? HITL_AMBER_LIGHT : '#fff',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="severity"
                  value={option.value}
                  checked={selected}
                  onChange={() => setSeverity(option.value)}
                  disabled={submitting}
                  style={{ marginRight: 8 }}
                />
                <span style={{ fontWeight: 600 }}>{option.label}</span>
                <span style={{ display: 'block', fontSize: 12, color: '#52525B', margin: '4px 0 0 24px' }}>
                  {option.description.startsWith('ako.') ? t(option.description, { name: t('companion.name') }) : option.description}
                </span>
              </label>
            );
          })}
        </fieldset>

        <label style={{ display: 'block', marginBottom: 16 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            What have you tried? <span style={{ color: '#71717A', fontWeight: 400 }}>(optional)</span>
          </span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={HITL_NOTE_MAX_LENGTH}
            rows={3}
            disabled={submitting}
            placeholder="For example: The second test still fails after I checked the input type."
            style={{
              width: '100%',
              padding: 10,
              borderRadius: 6,
              border: '1px solid #D4D4D8',
              fontSize: 14,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
          <span style={{ display: 'block', textAlign: 'right', fontSize: 11, color: '#71717A' }}>
            {note.length}/{HITL_NOTE_MAX_LENGTH}
          </span>
        </label>

        {error && (
          <div role="alert" style={{ padding: 12, marginBottom: 12, background: '#FEF2F2', color: '#B91C1C', borderRadius: 6, fontSize: 14 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{ padding: '10px 16px', minHeight: 44, borderRadius: 6, border: '1px solid #D4D4D8', background: '#fff', fontFamily: 'inherit' }}
          >
            Not now
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting}
            style={{
              padding: '10px 16px',
              minHeight: 44,
              borderRadius: 6,
              border: 0,
              background: HITL_AMBER,
              color: '#fff',
              opacity: submitting ? 0.65 : 1,
              fontFamily: 'inherit',
              fontWeight: 600,
            }}
          >
            {submitting ? 'Requesting…' : 'Request instructor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HitlRequestModal;
